/**
 * Controls of a mounted AI Teammate widget exposed in the browser console.
 */
export interface TeammateConsoleControls {
  /** Start listening, unless already listening or connecting */
  start: () => Promise<void>
  /** Stop listening */
  stop: () => Promise<void>
}

/** Controls of currently mounted AI Teammate widgets by widget ID */
const controlsByWidgetId = new Map<number, TeammateConsoleControls>()

/**
 * Register controls of a mounted AI Teammate widget, available in the browser console as
 * `window.constants.aiTeammateWidget.start(widgetId)` and `window.constants.aiTeammateWidget.stop(widgetId)`.
 * Returns a function unregistering the controls.
 */
export function registerTeammateConsoleControls(widgetId: number, controls: TeammateConsoleControls): () => void {
  installWindowApi()
  controlsByWidgetId.set(widgetId, controls)

  return () => {
    // Keep controls registered meanwhile by another widget instance with the same ID
    if (controlsByWidgetId.get(widgetId) === controls) {
      controlsByWidgetId.delete(widgetId)
    }
  }
}

/**
 * Expose start/stop functions on `window.constants.aiTeammateWidget`, preserving other flags.
 */
function installWindowApi() {
  if (typeof window === 'undefined') return

  window.constants = window.constants || {}
  const consoleApi = (window.constants.aiTeammateWidget = window.constants.aiTeammateWidget || {})
  consoleApi.start = widgetId => runControl(widgetId, 'start')
  consoleApi.stop = widgetId => runControl(widgetId, 'stop')
}

/**
 * Run the given control of a mounted widget, warning if no widget with such ID is mounted.
 */
async function runControl(widgetId: number, control: keyof TeammateConsoleControls) {
  const controls = controlsByWidgetId.get(Number(widgetId))
  if (!controls) {
    const mountedIds = [...controlsByWidgetId.keys()].join(', ') || 'none'
    console.warn(`[AI Teammate] Widget ${widgetId} is not mounted. Mounted teammate widgets: ${mountedIds}`)
    return
  }
  await controls[control]()
}
